import { Vector2 } from "../../../common/vector2.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { TransformValue } from "../../../transforms/transform_value.js";
import { Vfx2DManager } from "../../../webgl_2d/vfx_2d_manager.js";
import { Vfx2DRuntime } from "../../../webgl_2d/vfx_2d_runtime.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UICanvasRect } from "../../common_ui_elements/ui_canvas_rect.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { StarChestBox, StarChestArt } from "./star_chest_art.js";
import { AdventureRewardHud, AdventureRewardHudRefs } from "../adventure_screen/adventure_reward_hud.js";
import type { DailyTaskRewardDto, DailyTaskRewardType } from "../../../api/dto/daily_tasks.dto.js";
import { RewardFrame } from "../../common_ui_elements/reward_frame.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";

export function dailyTaskRewardIcon( type: DailyTaskRewardType ): ImgID
{
  switch ( type )
  {
    case "coin": return ImgID.UI_DailyTasksTaskCoins;
    case "heart": return ImgID.IconHeart;
    case "infinite_hearts": return ImgID.UI_ShopInfiniteHeart;
    case "no_ads": return ImgID.UI_RemoveAdsIcon;
    case "infinite_balls": return ImgID.UI_GameStartInfiniteBallsBooster;
    case "multiballs": return ImgID.UI_GameStartMultiballsBooster;
    case "big_balls": return ImgID.UI_GameStartBigBallBooster;
    case "fireworks": return ImgID.UI_GameStartFireworkBooster;
    case "mines": return ImgID.UI_PassiveBoosterBomb;
  }
}

export class StarChestOpenPopup
{
  private readonly ctx: CanvasRenderingContext2D;
  private readonly screen: UIElement;

  private flying_icons: UIImage[] = [];

  private readonly REWARD_GRID_COLS = 3;
  private readonly REWARD_SLOT_W = RewardFrame.DESIGN_W;
  private readonly REWARD_SLOT_H = RewardFrame.DESIGN_H;
  private readonly REWARD_COL_GAP = 28;
  private readonly REWARD_ROW_GAP = 42;
  private readonly REWARD_ICON_SIZE = 174;

  private readonly FLY_ICON_START_SCALE = 2;
  private readonly FLY_ICON_END_SCALE = 0.4;
  private readonly COIN_FLY_SPEED = 1.5;

  private readonly REWARD_BOX_TOP_GAP = 55;
  private readonly REWARD_GRID_BUTTON_GAP = 70;
  private readonly REWARD_BUTTON_W = 570;
  private readonly REWARD_BUTTON_H = 250;

  private readonly BOX_GLOW_SCALE = 2;
  private readonly BOX_GLOW_MAX_ALPHA = 0.5;
  private readonly BOX_GLOW_ROTATION_MS = 14000;

  private readonly BOX_GLOW_TINT: [number, number, number] = [1.0, 0.82, 0.35];
  private tinted_glow_bitmap: ImageBitmap | null = null;

  public getTintedGlowBitmap(): ImageBitmap | null
  {
    return this.tinted_glow_bitmap;
  }

  constructor( ctx: CanvasRenderingContext2D, screen: UIElement )
  {
    this.ctx = ctx;
    this.screen = screen;

    this.prepareTintedGlowBitmap();
  }

  private prepareTintedGlowBitmap(): void
  {
    const source = ImagesHolder.getImage( ImgID.RaysGlow1 );

    const off_canvas = document.createElement( "canvas" );
    off_canvas.width = source.width;
    off_canvas.height = source.height;
    // CPU-backed, so the readback below does not stall on the GPU.
    const off_ctx = off_canvas.getContext( "2d", { willReadFrequently: true } )!;
    off_ctx.drawImage( source, 0, 0 );

    const image_data = off_ctx.getImageData( 0, 0, off_canvas.width, off_canvas.height );
    const data = image_data.data;
    const [tint_r, tint_g, tint_b] = this.BOX_GLOW_TINT;

    for ( let i = 0; i < data.length; i += 4 )
    {
      data[i] = Math.min( 255, Math.floor( data[i] * tint_r ) );
      data[i + 1] = Math.min( 255, Math.floor( data[i + 1] * tint_g ) );
      data[i + 2] = Math.min( 255, Math.floor( data[i + 2] * tint_b ) );
    }

    off_ctx.putImageData( image_data, 0, 0 );
    createImageBitmap( off_canvas ).then( bitmap => { this.tinted_glow_bitmap = bitmap; } );
  }

  open( rewards: DailyTaskRewardDto[], onClosed?: () => void ): void
  {
    const screen_w = this.screen.getRect().getW();
    const screen_h = this.screen.getRect().getH();
    const center_x = screen_w / 2;
    const center_y = screen_h / 2;

    // `screen` is the popup layer and already spans the complete physical
    // canvas, including both safe areas. A second negative safe-area offset
    // would shift the backdrop upward and leave the bottom inset uncovered.
    const backdrop = new UICanvasRect(
      this.ctx,
      0,
      0,
      screen_w,
      screen_h,
      'rgba(0, 0, 0, 0.8)'
    );
    backdrop.setOpacityImmediate( 0 );
    backdrop.setOnClick( () => {} );
    this.screen.addChild( backdrop );

    const hud = AdventureRewardHud.build( this.ctx, this.screen, screen_w );
    hud.row.setOpacityImmediate( 0 );

    const glow = this.createBoxGlow();

    const popup = StarChestArt.createClosed( this.ctx, this.screen, center_x, center_y, 1 );
    popup.main.setOpacityImmediate( 0 );
    popup.top.setOpacityImmediate( 0 );

    const now = performance.now();
    const APPEAR_MS = 120;

    backdrop.fadeIn( APPEAR_MS, now, Easing.EaseOutQuad );
    popup.main.fadeIn( APPEAR_MS, now, Easing.EaseOutQuad );

    const top_fade_in = new TransformOpacity( popup.top, true, 0, 1, now, APPEAR_MS, { easing: Easing.EaseOutQuad } );
    top_fade_in.setOnFinished( () => this.playAnimation( backdrop, popup, glow, hud, center_x, center_y, rewards, onClosed ) );
    popup.top.addTransform( top_fade_in );
  }

  private playAnimation( backdrop: UICanvasRect, popup: StarChestBox, glow: UIElementHolder, hud: AdventureRewardHudRefs, center_x: number, center_y: number, rewards: DailyTaskRewardDto[], onClosed?: () => void ): void
  {
    const SQUASH_MS = 100;
    const UNSQUASH_MS = 100;
    const GROW_MS = 80;
    const SHRINK_MS = 100;

    const normal = new Vector2( 1, 1 );
    const squashed = new Vector2( 1, 0.55 );
    const grown = new Vector2( 1, 1.2 );

    const phase4_shrink = () =>
    {
      const start = performance.now();
      this.scaleFromBottom( popup.main, center_y, grown, normal, start, SHRINK_MS, Easing.EaseInQuad, () => this.showRewards( backdrop, popup, glow, hud, center_x, center_y, rewards, onClosed ) );
      this.scaleFromBottom( popup.top, center_y, grown, normal, start, SHRINK_MS, Easing.EaseInQuad );
    };

    const phase3_grow = () =>
    {
      StarChestArt.swapCapToOpen( popup.top, center_x, center_y, 1 );

      const start = performance.now();
      this.scaleFromBottom( popup.main, center_y, normal, grown, start, GROW_MS, Easing.EaseOutQuad, phase4_shrink );
      this.scaleFromBottom( popup.top, center_y, normal, grown, start, GROW_MS, Easing.EaseOutQuad );
    };

    const phase2_unsquash = () =>
    {
      const start = performance.now();
      this.scaleFromBottom( popup.main, center_y, squashed, normal, start, UNSQUASH_MS, Easing.EaseOutQuad, phase3_grow );
      this.scaleFromBottom( popup.top, center_y, squashed, normal, start, UNSQUASH_MS, Easing.EaseOutQuad );
    };

    const start = performance.now();
    this.scaleFromBottom( popup.main, center_y, normal, squashed, start, SQUASH_MS, Easing.EaseInQuad, phase2_unsquash );
    this.scaleFromBottom( popup.top, center_y, normal, squashed, start, SQUASH_MS, Easing.EaseInQuad );
  }

  private scaleFromBottom( target: UIElement, ground_y: number, from_scale: Vector2, to_scale: Vector2, start_time: number, duration_ms: number, easing: Easing, on_complete?: () => void ): void
  {
    const center_y = target.pixelPosImportant().y + target.getRect().getH() / 2;
    const d = center_y - ground_y;

    const scale_transform = new TransformScale( target, true, from_scale, to_scale, start_time, duration_ms, { easing } );
    if ( on_complete )
      scale_transform.setOnFinished( on_complete );
    target.addTransform( scale_transform );

    const from_offset = new Vector2( 0, d * ( from_scale.y - 1 ) );
    const to_offset = new Vector2( 0, d * ( to_scale.y - 1 ) );
    target.addTransform( new TransformStraightMove( target, false, from_offset, to_offset, start_time, duration_ms, { easing } ) );
  }

  private showRewards( backdrop: UICanvasRect, popup: StarChestBox, glow: UIElementHolder, hud: AdventureRewardHudRefs, center_x: number, center_y: number, rewards: DailyTaskRewardDto[], onClosed?: () => void ): void
  {
    const RISE_MS = 280;
    const REVEAL_MS = 210;

    const now = performance.now();

    const cap_top_offset = LayoutScaler.heightFromDesign( StarChestArt.MAIN_H ) + LayoutScaler.yFromDesign( StarChestArt.OPEN_OFFSET_Y );
    const box_top_gap = LayoutScaler.yFromDesign( this.REWARD_BOX_TOP_GAP );
    const panel_h = this.rewardsPanelHeight( rewards.length );

    const block_h = cap_top_offset + box_top_gap + panel_h;
    const block_top = this.screen.getRect().getH() / 2 - block_h / 2;
    const rise_offset = center_y - ( block_top + cap_top_offset );

    const main_target = new Vector2( popup.main.pixelPosImportant().x, popup.main.pixelPosImportant().y - rise_offset );
    const top_target = new Vector2( popup.top.pixelPosImportant().x, popup.top.pixelPosImportant().y - rise_offset );

    popup.main.addTransform( new TransformStraightMove( popup.main, true, null, main_target, now, RISE_MS, { easing: Easing.EaseOutQuad } ) );
    popup.top.addTransform( new TransformStraightMove( popup.top, true, null, top_target, now, RISE_MS, { easing: Easing.EaseOutQuad } ) );

    const risen_box_bottom_y = center_y - rise_offset;
    const box_center_y = risen_box_bottom_y - cap_top_offset / 2;

    this.startBoxGlow( glow, center_x, box_center_y, RISE_MS );

    const { panel, claim_button, slot_icons } = this.createRewardsPanel( rewards, center_x, risen_box_bottom_y );

    panel.setOpacityImmediate( 0 );
    panel.fadeIn( REVEAL_MS, now + RISE_MS * 0.4, Easing.EaseOutQuad );

    const HUD_APPEAR_MS = 120;

    claim_button.setOnClick( () =>
    {
      claim_button.setClickable( false );
      hud.row.fadeIn( HUD_APPEAR_MS, performance.now(), Easing.EaseOutQuad );
      const fly_ms = this.flyRewardsToHud( rewards, slot_icons, hud );
      window.setTimeout( () => this.close( backdrop, popup, glow, panel, hud, onClosed ), fly_ms + 260 );
    } );
  }

  private flyRewardsToHud( rewards: DailyTaskRewardDto[], slot_icons: UIImage[], hud: AdventureRewardHudRefs ): number
  {
    Game.instance.setVfxOverlayCanvasesVisible( true );

    const now = performance.now();
    const icon_size = LayoutScaler.widthFromDesign( this.REWARD_ICON_SIZE * 0.75 );

    let max_finish = now;
    let heart_finish = 0;
    let avatar_finish = 0;

    rewards.forEach( ( reward, i ) =>
    {
      const from = this.absoluteCenter( slot_icons[i] );

      if ( reward.type === "coin" )
      {
        const count = Math.max( 1, Math.min( 8, Math.round( reward.amount / 3 ) ) );
        const finish = Vfx2DManager.addCoinsFly( from, hud.coin.target, count, now, this.COIN_FLY_SPEED );
        max_finish = Math.max( max_finish, finish );
        return;
      }

      const is_heart_like = reward.type === "heart" || reward.type === "infinite_hearts";
      const target = is_heart_like ? hud.heart.target : hud.avatar_target;
      const count = is_heart_like ? Math.min( 3, Math.max( 1, reward.amount ) ) : 1;

      for ( let n = 0; n < count; n++ )
      {
        const finish = this.flyIcon( dailyTaskRewardIcon( reward.type ), from, target, icon_size, now + n * 60 );
        max_finish = Math.max( max_finish, finish );
        if ( is_heart_like ) heart_finish = Math.max( heart_finish, finish );
        else avatar_finish = Math.max( avatar_finish, finish );
      }
    } );

    if ( heart_finish > 0 )
      Vfx2DManager.addCoinCounterImpact( hud.heart.target, icon_size, heart_finish, 280 );
    if ( avatar_finish > 0 )
      Vfx2DManager.addCoinCounterImpact( hud.avatar_target, icon_size * 1.3, avatar_finish, 280 );

    return max_finish - now;
  }

  private flyIcon( img_id: ImgID, from: Vector2, to: Vector2, size: number, start_time: number ): number
  {
    const DURATION_MS = 410;

    const icon = new UIImage( this.ctx, from.x - size / 2, from.y - size / 2, size, size, img_id );
    this.screen.addChild( icon );
    this.flying_icons.push( icon );

    const side = Math.random() < 0.5 ? -1 : 1;
    const mid_x = ( from.x + to.x ) / 2 + side * ( 40 + Math.random() * 50 );
    const mid_y = Math.min( from.y, to.y ) - ( 60 + Math.random() * 50 );

    const path = ( t: number ): Vector2 =>
    {
      const inv = 1 - t;
      const x = inv * inv * from.x + 2 * inv * t * mid_x + t * t * to.x;
      const y = inv * inv * from.y + 2 * inv * t * mid_y + t * t * to.y;
      return new Vector2( x - size / 2, y - size / 2 );
    };
    const apply = ( t: number ): void => icon.setPixelPos( path( t ), true );

    const fly = new TransformValue( 0, 1, start_time, DURATION_MS, apply, { easing: Easing.EaseInQuad } );
    fly.setDomSample( t => ( { pos: path( t ) } ) );
    icon.addTransform( fly );
    const start_scale = new Vector2( this.FLY_ICON_START_SCALE, this.FLY_ICON_START_SCALE );
    const end_scale = new Vector2( this.FLY_ICON_END_SCALE, this.FLY_ICON_END_SCALE );
    icon.addTransform( new TransformScale( icon, true, start_scale, end_scale, start_time, DURATION_MS, { easing: Easing.EaseInQuad } ) );

    const fade = new TransformOpacity( icon, true, 1, 0, start_time + DURATION_MS * 0.75, DURATION_MS * 0.25, { easing: Easing.EaseInQuad } );
    fade.setOnFinished( () =>
    {
      this.screen.removeChild( icon );
      const idx = this.flying_icons.indexOf( icon );
      if ( idx > -1 ) this.flying_icons.splice( idx, 1 );
    } );
    icon.addTransform( fade );

    return start_time + DURATION_MS;
  }

  private absoluteCenter( el: UIElement ): Vector2
  {
    const pos = el.getAbsolutePosition();
    const rect = el.getRect();
    return new Vector2( pos.x + rect.getW() / 2, pos.y + rect.getH() / 2 );
  }

  private createBoxGlow(): UIElementHolder
  {
    const size = LayoutScaler.widthFromDesign( StarChestArt.OPEN_W * this.BOX_GLOW_SCALE );

    const glow = new UIElementHolder( this.ctx, 0, 0, size, size, false );
    glow.setOpacityImmediate( 0 );

    const now = performance.now();
    for ( const direction of [1, -1] )
    {
      const layer = new UIImage( this.ctx, 0, 0, size, size, ImgID.RaysGlow1 );
      if ( this.tinted_glow_bitmap )
        layer.setImageBitmap( this.tinted_glow_bitmap );

      layer.enableRotation();
      layer.addTransform( new TransformRotate(
        layer, true, 0, direction * Math.PI * 2, now, this.BOX_GLOW_ROTATION_MS,
        { easing: Easing.Linear, infinite_loop: true, decorative: true }
      ) );

      glow.addChild( layer );
    }

    this.screen.addChild( glow );
    return glow;
  }

  private startBoxGlow( glow: UIElementHolder, center_x: number, center_y: number, appear_ms: number ): void
  {
    const size = LayoutScaler.widthFromDesign( StarChestArt.OPEN_W * this.BOX_GLOW_SCALE );
    glow.setPixelPos( new Vector2( center_x - size / 2, center_y - size / 2 ), true );
    glow.fadeToOpacity( this.BOX_GLOW_MAX_ALPHA, appear_ms, performance.now(), Easing.EaseOutQuad );
  }

  private rewardsPanelHeight( reward_count: number ): number
  {
    const rows = Math.ceil( reward_count / this.REWARD_GRID_COLS );
    const slot_h = LayoutScaler.heightFromDesign( this.REWARD_SLOT_H );
    const row_gap = LayoutScaler.heightFromDesign( this.REWARD_ROW_GAP );
    const grid_h = rows * slot_h + Math.max( 0, rows - 1 ) * row_gap;

    const button_h = LayoutScaler.heightFromDesign( this.REWARD_BUTTON_H );
    const grid_button_gap = LayoutScaler.yFromDesign( this.REWARD_GRID_BUTTON_GAP );

    return grid_h + grid_button_gap + button_h;
  }

  private createRewardsPanel( rewards: DailyTaskRewardDto[], center_x: number, box_bottom_y: number ): { panel: UIElementHolder, claim_button: UIImage, slot_icons: UIImage[] }
  {
    const rows = Math.ceil( rewards.length / this.REWARD_GRID_COLS );
    const slot_w = LayoutScaler.widthFromDesign( this.REWARD_SLOT_W );
    const slot_h = LayoutScaler.heightFromDesign( this.REWARD_SLOT_H );
    const col_gap = LayoutScaler.widthFromDesign( this.REWARD_COL_GAP );
    const row_gap = LayoutScaler.heightFromDesign( this.REWARD_ROW_GAP );

    const grid_w = this.REWARD_GRID_COLS * slot_w + ( this.REWARD_GRID_COLS - 1 ) * col_gap;
    const grid_h = rows * slot_h + Math.max( 0, rows - 1 ) * row_gap;

    const button_w = LayoutScaler.widthFromDesign( this.REWARD_BUTTON_W );
    const button_h = LayoutScaler.heightFromDesign( this.REWARD_BUTTON_H );

    const box_top_gap = LayoutScaler.yFromDesign( this.REWARD_BOX_TOP_GAP );
    const grid_button_gap = LayoutScaler.yFromDesign( this.REWARD_GRID_BUTTON_GAP );

    const panel_w = Math.max( grid_w, button_w );
    const panel_h = grid_h + grid_button_gap + button_h;

    const panel_x = center_x - panel_w / 2;
    const panel_y = box_bottom_y + box_top_gap;

    const panel = new UIElementHolder( this.ctx, panel_x, panel_y, panel_w, panel_h, false );

    const grid_x = ( panel_w - grid_w ) / 2;
    const slot_icons: UIImage[] = rewards.map( ( reward, i ) =>
    {
      const row = Math.floor( i / this.REWARD_GRID_COLS );
      const col = i % this.REWARD_GRID_COLS;

      const items_in_row = Math.min( this.REWARD_GRID_COLS, rewards.length - row * this.REWARD_GRID_COLS );
      const row_w = items_in_row * slot_w + ( items_in_row - 1 ) * col_gap;
      const row_x = grid_x + ( grid_w - row_w ) / 2;

      return this.createRewardSlot( panel, reward, row_x + col * ( slot_w + col_gap ), row * ( slot_h + row_gap ) );
    } );

    const button_x = ( panel_w - button_w ) / 2;
    const button_y = grid_h + grid_button_gap;
    const claim_button = this.createClaimButton( panel, button_x, button_y, button_w, button_h );

    this.screen.addChild( panel );

    return { panel, claim_button, slot_icons };
  }

  private createRewardSlot( panel: UIElement, reward: DailyTaskRewardDto, x: number, y: number ): UIImage
  {
    const is_coin = reward.type === "coin";
    const icon_w = LayoutScaler.widthFromDesign( is_coin ? 210 : this.REWARD_ICON_SIZE );
    const icon_h = LayoutScaler.heightFromDesign( is_coin ? 168 : this.REWARD_ICON_SIZE );
    const frame = new RewardFrame( this.ctx, x, y, {
      icon_id: is_coin ? ImgID.UI_ShopBlockImg : dailyTaskRewardIcon( reward.type ),
      amount_text: `${reward.amount}`,
      icon_width: icon_w,
      icon_height: icon_h,
    } );
    RewardTooltip.attachLegacy( frame, reward.type );
    panel.addChild( frame );

    return frame.icon;
  }

  private createClaimButton( panel: UIElement, x: number, y: number, w: number, h: number ): UIImage
  {
    const button = new UIImage( this.ctx, x, y, w, h, ImgID.UI_PlayButton, {}, true );

    const text = new UITextElement( this.ctx, 0, 0, w, h, {
      localization_key: "CLAIM",
      font_size: LayoutScaler.textSizeFromDesign( 67 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 800,
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_color: ColorScheme.black_transparent,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      outline_color: ColorScheme.more_lives_buy_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } );

    button.addChild( text, true );
    panel.addChild( button );

    return button;
  }

  private close( backdrop: UICanvasRect, popup: StarChestBox, glow: UIElementHolder, panel: UIElementHolder, hud: AdventureRewardHudRefs, onClosed?: () => void ): void
  {
    const FADE_OUT_MS = 130;
    const now = performance.now();

    backdrop.fadeOut( FADE_OUT_MS, now, Easing.EaseInQuad );
    popup.main.fadeOut( FADE_OUT_MS, now, Easing.EaseInQuad );
    popup.top.fadeOut( FADE_OUT_MS, now, Easing.EaseInQuad );
    glow.fadeOut( FADE_OUT_MS, now, Easing.EaseInQuad );
    hud.row.fadeOut( FADE_OUT_MS, now, Easing.EaseInQuad );

    const panel_fade_out = new TransformOpacity( panel, true, 1, 0, now, FADE_OUT_MS, { easing: Easing.EaseInQuad } );
    panel_fade_out.setOnFinished( () =>
    {
      this.screen.removeChild( backdrop );
      this.screen.removeChild( popup.main );
      this.screen.removeChild( popup.top );
      this.screen.removeChild( glow );
      this.screen.removeChild( hud.row );
      this.screen.removeChild( panel );

      for ( const icon of this.flying_icons )
        this.screen.removeChild( icon );
      this.flying_icons = [];

      try
      {
        if ( !Vfx2DRuntime.hasActiveEffects() )
          Game.instance.setVfxOverlayCanvasesVisible( false );
      }
      catch ( e )
      {
        console.warn( "StarChestOpenPopup.close: hasActiveEffects() check failed, leaving VFX overlay canvases as-is", e );
      }

      onClosed?.();
      // removeChild() does not invalidate the retained canvas by itself. The
      // previous fade-out frame would otherwise remain visible as translucent
      // popup trails until some unrelated UI change triggers a redraw.
      this.screen.forceMakeShouldBeRendered();
    } );
    panel.addTransform( panel_fade_out );
  }
}
